/*
 * Prescription Grid Component - Professional Design
 */

import React, {useState, useEffect} from 'react';
import {
    View,
    Heading,
    Text,
    ProgressCircle,
    Button,
    Flex,
    TableView,
    TableHeader,
    TableBody,
    Column,
    Row,
    Cell,
    IllustratedMessage,
    Content,
    Well,
    Divider
} from '@adobe/react-spectrum';
import Refresh from '@spectrum-icons/workflow/Refresh';
import UploadToCloud from '@spectrum-icons/workflow/UploadToCloud';
import {useAuth} from '../context/AuthContext';
import {usePrescriptions} from "../hooks/usePrescriptions";
import {getPresignedUrl} from '../utils/api'; 


export function PrescriptionGrid({onRefresh, onUploadClick, ims, onEditClick}) {

    const [error, setError] = useState(null);
    const {customerData, logout} = useAuth();
    const {data, isLoading, refresh, isError, update, remove, refetch} = usePrescriptions(customerData, ims);
    const [deletingId, setDeletingId] = useState(null);
    const [drawerFile, setDrawerFile] = useState(null);
    const [drawerUrl, setDrawerUrl] = useState(null);
    const [isDrawerLoading, setIsDrawerLoading] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    useEffect(() => {
        const isMobile = window.matchMedia('(max-width: 767px)').matches;

        if (!isMobile) return;

        if (isDrawerOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none';
        } else {
            document.body.style.overflow = '';
            document.body.style.touchAction = '';
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.touchAction = '';
        };
        }, [isDrawerOpen]);



   const handleViewPrescription = async (fileUrl) => {
        if (!fileUrl) return;

        setDrawerFile(fileUrl);
            setDrawerUrl(null); 
            setIsDrawerLoading(true);
            setIsDrawerOpen(true);

        try {
            const { url } = await getPresignedUrl(fileUrl, ims);
            setDrawerUrl(url);
        } catch (err) {
            console.error(err);
            alert('Failed to load prescription');
        } finally {
            setIsDrawerLoading(false);
        }
    };

    const closeDrawer = () => {
        setIsDrawerOpen(false);

        setTimeout(() => {
            setDrawerUrl(null);
            setDrawerFile(null);
        }, 350); 
    };

    const handleEdit = (prescription) => {
    if (onEditClick) {
      onEditClick(prescription);
    }
  };

    const handleDelete = async (id, fileUrl) => {
        if (!window.confirm('Are you sure you want to delete this prescription?')) return;
        setDeletingId(id);
        try {
            await remove.mutateAsync({ id, fileUrl });
        } catch (err) {
            alert('Failed to delete prescription');
        }finally {
        setDeletingId(null);
    }
    };


    const handleRefresh = () => {
        refresh();
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };


    const getStatusBadge = (status) => {
        const statusColors = {
            submitted: {bg: '#1473E6', color: '#fff'},
            approved: {bg: '#2D9D78', color: '#fff'},
            rejected: {bg: '#D7373F', color: '#fff'},
            pending: {bg: '#E68619', color: '#fff'}
        };

        const statusKey = (status || 'submitted').toLowerCase();
        const colors = statusColors[statusKey] || statusColors.submitted;

        return (
            <View
                UNSAFE_style={{
                    backgroundColor: colors.bg,
                    color: colors.color,
                    padding: '4px 12px',
                    borderRadius: '12px',
                    display: 'inline-block',
                    fontSize: '13px',
                    fontWeight: '600',
                    textTransform: 'capitalize'
                }}
            >
                {status || 'Submitted'}
            </View>
        );
    };

    if (isLoading) {
        return (
            <View padding="size-200" width="100%">
                <Flex direction="column" alignItems="center" gap="size-200" marginTop="size-800">
                    <ProgressCircle aria-label="Loading prescriptions" isIndeterminate size="L"/>
                    <Text UNSAFE_style={{fontSize: '16px', color: '#6e6e6e'}}>
                        Loading your prescriptions...
                    </Text>
                </Flex>
            </View>
        );
    }

    if (isError) {
        return (
            <View padding="size-200" width="100%">
                <Flex direction="column" gap="size-300" alignItems="center" marginTop="size-600">
                    <IllustratedMessage>
                        <Heading>Error Loading Prescriptions</Heading>
                        <Content>{error}</Content>
                    </IllustratedMessage>
                    <Button variant="primary" onPress={handleRefresh}>
                        <Refresh/>
                        <Text>Try Again</Text>
                    </Button>
                </Flex>
            </View>
        );
    }

    // Empty State - Show centered upload button
    if (!isLoading && (!data?.prescriptions || data.prescriptions.length === 0)) {
        return (
            <View width="100%" minHeight="80vh" padding="size-200">
                <Flex
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="80vh"
                    gap="size-400"
                >
                    <Flex direction="column" gap="size-200" alignItems="center" maxWidth="size-6000">
                        <Heading level={1} UNSAFE_style={{
                            fontSize: 'clamp(24px, 5vw, 32px)',
                            marginBottom: '8px',
                            textAlign: 'center'
                        }}>
                            No Prescriptions Yet
                        </Heading>
                        <Text UNSAFE_style={{
                            fontSize: 'clamp(14px, 3vw, 16px)',
                            color: '#6e6e6e',
                            textAlign: 'center',
                            padding: '0 16px'
                        }}>
                            Upload your first prescription to get started managing your prescriptions
                        </Text>
                    </Flex>
                    <Button
                        variant="cta"
                        onPress={onUploadClick}
                        UNSAFE_style={{
                            fontSize: 'clamp(14px, 3vw, 18px)',
                            padding: 'clamp(12px, 2vw, 16px) clamp(24px, 5vw, 48px)',
                            minHeight: 'clamp(48px, 10vw, 56px)',
                            fontWeight: '600'
                        }}
                    >
                        <UploadToCloud size="L"/>
                        <Text>Upload Your First Prescription</Text>
                    </Button>
                </Flex>
            </View>
        );
    }


    // Grid with data - Show upload button in top right
    const columns = [
        {name: 'ID', uid: 'id'},
        {name: 'Name', uid: 'customerName'},
        {name: 'Date of Birth', uid: 'dob'},
        {name: 'Order #', uid: 'orderNumber'},
        {name: 'Actions', uid: 'actions', align: 'end'},
    ];

    const rows = data.prescriptions.map((prescription, index) => ({
        id: prescription.id || prescription.internalId || index,
        prescriptionId: prescription.id || prescription.internalId,
        customerName: prescription.name,
        dob: prescription.dob,
        orderNumber: prescription.order_number || '—',
        fileUrl: prescription.file_url || null,
        presignedUrl: prescription.presigned_url || null,
        edit: prescription.edit,
        delete: prescription.delete,
        rawData: prescription
    }));


    // Render card view for mobile
    const renderMobileCards = () => (
        <Flex direction="column" gap="size-200">
            {rows.map((item) => (
                <Well key={item.id} role="region" aria-label={`Prescription ${item.prescriptionId}`}>
                    <Flex direction="column" gap="size-150">
                        {/* ID and Status */}
                        <Flex direction="row" justifyContent="space-between" alignItems="center" wrap gap="size-100">
                            <Text UNSAFE_style={{fontWeight: 600, fontSize: '16px'}}>
                                #{item.prescriptionId}
                            </Text>
                            {getStatusBadge(item.status)}
                        </Flex>

                        <Divider size="S"/>

                        {/* Details */}
                        <Flex direction="column" gap="size-100">
                           
                            <Flex direction="row" justifyContent="space-between" gap="size-100">
                                <Text UNSAFE_style={{fontSize: '13px', color: '#6e6e6e'}}>Name:</Text>
                                <Text UNSAFE_style={{fontSize: '13px', fontWeight: 500}}>{item.customerName}</Text>
                            </Flex>
                            <Flex direction="row" justifyContent="space-between" gap="size-100">
                                <Text UNSAFE_style={{fontSize: '13px', color: '#6e6e6e'}}>Date of Birth:</Text>
                                <Text UNSAFE_style={{fontSize: '13px', fontWeight: 500}}>{item.dob}</Text>
                            </Flex>

                            {item.orderNumber !== '—' && (
                                <Flex direction="row" justifyContent="space-between" gap="size-100">
                                    <Text UNSAFE_style={{fontSize: '13px', color: '#6e6e6e'}}>Order #:</Text>
                                    <Text UNSAFE_style={{fontSize: '13px', fontWeight: 500, fontFamily: 'monospace'}}>
                                        {item.orderNumber}
                                    </Text>
                                </Flex>
                            )}

                        </Flex>

                        {/* Action Button */}
                        {item.fileUrl && (
                            <>
                                <Divider size="S"/>
                                <Button
                                    variant="primary"
                                    onPress={() => handleViewPrescription(item.fileUrl)}
                                    width="100%"
                                >
                                    {drawerFile === item.fileUrl ? 'Loading...' : 'View'}
                                </Button>
                            </>
                        )}
                        <Button
                            variant="primary"
                            onPress={() => handleEdit(item.rawData)}
                            width="100%"
                        >
                            Edit
                        </Button>
                        <Button
                            variant="negative"
                            onPress={() => handleDelete(item.prescriptionId, item.fileUrl)}
                            isDisabled={deletingId === item.prescriptionId}
                            isLoading={deletingId === item.prescriptionId}
                            width="100%"
                        >
                            Delete
                        </Button>
                    </Flex>
                </Well>
            ))}
        </Flex> 
    );

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    {isDrawerOpen && isMobile && (
        <View
            UNSAFE_style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            zIndex: 999,
            touchAction: 'none'
            }}
            onClick={closeDrawer}
        />
        )}

    // mobile drawer
    const renderMobileDrawer = () => (
        <View
            UNSAFE_style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '80vh',
            backgroundColor: '#fff',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            boxShadow: '0 -8px 24px rgba(0,0,0,0.2)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',

            transform: isDrawerOpen ? 'translateY(0)' : 'translateY(100%)',
            transition: 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
            willChange: 'transform',
            pointerEvents: drawerFile ? 'auto' : 'none'
            }}
        >
            <Flex justifyContent="space-between" alignItems="center" marginY={3} marginX={20}>
            <Heading level={3}>
                Prescription
            </Heading>
            <Button variant="secondary" isQuiet onPress={closeDrawer}>
                Close
            </Button>
            </Flex>

            <Divider size="S" />
            

            <View
            flex
            padding="size-10"
            UNSAFE_style={{
                overflowY: 'auto',
                overflowX: 'hidden',        
                width: '100%',
                maxWidth: '100%',
                WebkitOverflowScrolling: 'touch', 
            }}
            >
            {!drawerUrl ? (
                <Flex
                direction="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                gap="size-100"
                >
                <ProgressCircle aria-label="Loading" isIndeterminate />
                <Text>Loading prescription...</Text>
                </Flex>
            ) : (
                <View
                UNSAFE_style={{
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                }}
                >
                <iframe
                    src={drawerUrl}
                    title="Prescription Viewer"
                    style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    flex: 1,
                    display: 'block',
                    }}
                />
                </View>
            )}
            </View>

        </View>
    );

    // Render table view for desktop
    const renderDesktopTable = () => (
        <View
            UNSAFE_style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e1e1e1',
                overflow: 'auto',
                width: '100hv',
            }}
        >
            <TableView
                aria-label="Prescription history table"
                width="100hv"
                density="regular"
                overflowMode="wrap"
            >
                <TableHeader columns={columns}>
                    {(column) => (
                        <Column key={column.uid} align={column.align}>
                            {column.name}
                        </Column>
                    )}
                </TableHeader>
                <TableBody items={rows}>
                    {(item) => (
                        <Row key={item.id}>
                            {(columnKey) => {
                                if (columnKey === 'id') {
                                    return (
                                        <Cell>
                                            <Text UNSAFE_style={{fontWeight: 500}}>
                                                #{item.prescriptionId}
                                            </Text>
                                        </Cell>
                                    );
                                }
                                if (columnKey === 'orderNumber') {
                                    return (
                                        <Cell>
                                            <Text UNSAFE_style={{fontFamily: 'monospace'}}>
                                                {item.orderNumber}
                                            </Text>
                                        </Cell>
                                    );
                                }
                                if (columnKey === 'status') {
                                    return <Cell>{getStatusBadge(item.status)}</Cell>;
                                }
                                if (columnKey === 'fileName') {
                                    return (
                                        <Cell>
                                            <Text UNSAFE_style={{fontSize: '13px'}}>
                                                {item.fileName}
                                            </Text>
                                        </Cell>
                                    );
                                }
                                if (columnKey === 'actions') {
                                    return (
                                        <Cell>
                                            <Flex UNSAFE_style={{gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end'}}>
                                            {item.fileUrl && (
                                                <Button
                                                    variant="primary"
                                                    onPress={() => handleViewPrescription(item.fileUrl)}
                                                    isQuiet
                                                >
                                                    View
                                                </Button>
                                            )}
                                            <Button
                                                variant="primary"
                                                onPress={() => handleEdit(item.rawData)}
                                                isQuiet
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                variant="negative"
                                                onPress={() => handleDelete(item.prescriptionId, item.fileUrl)}
                                                isQuiet
                                                isDisabled={deletingId === item.prescriptionId}
                                                isLoading={deletingId === item.prescriptionId}
                                            >
                                                {deletingId === item.prescriptionId ? "Deleting..." : "Delete"}
                                            </Button>
                                            </Flex>
                                        </Cell>
                                    );
                                }
                                return <Cell>{item[columnKey]}</Cell>;
                            }}
                        </Row>
                    )}
                </TableBody>
            </TableView>
            </View>      
    );

    // desktop drawer
    const renderDesktopDrawer = () => (
        <View
            UNSAFE_style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '50%',
            height: '100%',
            backgroundColor: '#f8f8f8',
            boxShadow: isDrawerOpen ? '-8px 0 24px rgba(0,0,0,0.15)' : 'none',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',

            transform: isDrawerOpen ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
            willChange: 'transform',
            pointerEvents: drawerFile ? 'auto' : 'none'
            }}
        >
            <Flex justifyContent="space-between" alignItems="center" marginY={3} marginX={20}>
            <Heading level={3}>
                Prescription File
            </Heading>
            <Button variant="secondary" isQuiet onPress={closeDrawer}>
                Close
            </Button>
            </Flex>

            <Divider size="S" />

            <View
                flex
                padding="size-100"
                UNSAFE_style={{
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    width: '100%',
                    maxWidth: '100%',
                }}
                >
                {!drawerUrl ? (
                    <Flex alignItems="center" justifyContent="center" height="100%">
                    <ProgressCircle aria-label="Loading" isIndeterminate />
                    <Text marginStart="size-150">Loading...</Text>
                    </Flex>
                ) : (
                    <View
                    UNSAFE_style={{
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                    }}
                    >
                    <iframe
                        src={drawerUrl}
                        title="Prescription Viewer"
                        style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        flex: 1,
                        display: 'block',
                        }}
                    />
                    </View>
                )}
                </View>

        </View>
    );

    return (
    <View
        padding="size-200"
        UNSAFE_style={{
        position: 'relative',
        width: '100%',
        height: '100%',        
        minHeight: '100vh',   
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    }}
    >
        {/* Header */}
        <Flex
        direction={{ base: 'column', M: 'row' }}
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', M: 'center' }}
        gap="size-200"
        marginBottom="size-300"
        wrap
        UNSAFE_style={{
            borderBottom: '2px solid #e1e1e1',
            paddingBottom: '16px'
        }}
        >
            <Flex direction="column" gap="size-100" flex={1}>
                <Heading level={2} margin={0}>
                My Prescriptions
                </Heading>
                <Text UNSAFE_style={{ fontSize: '14px', color: '#6e6e6e' }}>
                {data.prescriptions.length}{' '}
                {data.prescriptions.length === 1 ? 'prescription' : 'prescriptions'} found
                </Text>
            </Flex>

            <Flex direction="row" gap="size-200" wrap>
                <Button variant="secondary" onPress={handleRefresh} isQuiet>
                <Refresh />
                <Text>Refresh</Text>
                </Button>
                <Button variant="cta" onPress={onUploadClick}>
                <UploadToCloud />
                <Text>Upload Prescription</Text>
                </Button>
            </Flex>
        </Flex>

        {/* Main Content */}
        <View overflow="auto"
            UNSAFE_style={{
            flex: 1,
            overflow: 'auto',
        }}
        >
            {/* Mobile */}
            <View isHidden={{ base: false, M: true }}>
                {renderMobileCards()}
            </View>

            {/* Desktop */}
            <View isHidden={{ base: true, M: false }}>
                {renderDesktopTable()}
            </View>
        </View>
        
            <View isHidden={{ base: false, M: true }}>
                {renderMobileDrawer()}
            </View>
            <View isHidden={{ base: true, M: false }}>
                {renderDesktopDrawer()}
            </View>
</View>
);
}
